import { Injectable, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, debounceTime, skip } from 'rxjs/operators';
import { TourGuideTooltipService } from './tour-guide-tooltip.service';
import { TooltipAnchorService } from './tooltip-anchor.service';
import { TooltipInjectorService } from './tooltip-injector.service';
import { TooltipUtilsService } from './tooltip-utils.service';

const CHECK_INTERVAL_MS = 200;
const MAX_WALL_MS       = 10_000; // Option D: wall-clock cap instead of fixed attempt count

@Injectable({ providedIn: 'root' })
export class TooltipReanchorService implements OnDestroy {

  private observers    = new Map<string, MutationObserver>();
  private routerSub!: Subscription;
  private enabledSub?: Subscription;
  private checkId: ReturnType<typeof setInterval> | null = null;
  private reanchorTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private static readonly REANCHOR_DEBOUNCE_MS = 150;

  constructor(
    private router: Router,
    private tooltipService: TourGuideTooltipService,
    private anchorService: TooltipAnchorService,
    private injectorService: TooltipInjectorService,
    private utils: TooltipUtilsService
  ) {}

  initialize(): void {
    this.routerSub = this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      debounceTime(100)
    ).subscribe(() => {
      this.disconnectStaleObservers();
      if (!this.tooltipService.isTooltipEnabled$.value) return;
      const route = this.utils.normalizeRoute(this.router.url.split('?')[0]);
      this.tooltipService.loadTooltipsForRoute(route).subscribe({
        next: () => this.startPageLoadCheck(),
        error: () => this.startPageLoadCheck()
      });
    });

    this.enabledSub = this.tooltipService.isTooltipEnabled$.pipe(skip(1)).subscribe(enabled => {
      if (enabled) {
        const route = this.utils.normalizeRoute(this.router.url.split('?')[0]);
        this.tooltipService.loadTooltipsForRoute(route).subscribe({
          next: () => this.startPageLoadCheck(),
          error: () => this.startPageLoadCheck()
        });
      }
    });

    if (this.tooltipService.isTooltipEnabled$.value) {
      this.startPageLoadCheck();
    }
  }

  // ─── Page-load polling (Options D + E) ───────────────────────
  // Option D: polls until all component hosts exist and all icons are resolved,
  // up to a MAX_WALL_MS wall-clock limit. Attempts against groups whose host
  // is not yet in the DOM do NOT count toward giving up.
  // Option E: eagerly resolves hoverAnchors alongside icon anchors so the
  // container is pre-marked before the user first clicks an icon.

  private startPageLoadCheck(): void {
    this.stopPageLoadCheck();
    this.injectorService.clearDetachedInstances();

    const route     = this.utils.normalizeRoute(this.router.url.split('?')[0]);
    const startTime = Date.now();

    this.checkId = setInterval(() => {
      // Option D: hard wall-clock cap
      if (Date.now() - startTime > MAX_WALL_MS) {
        this.stopPageLoadCheck();
        this.syncObservers();
        return;
      }

      const tooltips = this.tooltipService.getAllTooltips()
        .filter(t => t.isEnabled !== false)
        .filter(t => t.anchor?.route === route);

      if (tooltips.length === 0) return;

      // Group by component tag so we can skip groups whose host isn't rendered yet
      const byComponent = new Map<string, typeof tooltips>();
      tooltips.forEach(t => {
        const tag = t.anchor.componentTag;
        if (!byComponent.has(tag)) byComponent.set(tag, []);
        byComponent.get(tag)!.push(t);
      });

      let allDisplayed = true;

      byComponent.forEach((group, tag) => {
        const hostSelector = tag === 'body' ? 'body' : tag;
        const host = document.querySelector(hostSelector);

        // Option D: host not in DOM yet — don't burn the budget, just skip
        if (!host) { allDisplayed = false; return; }

        group.forEach(config => {
          const iconPresent = !!document.querySelector(
            `.tour-guide-question-icon[data-tooltip-id="${config.id}"]`
          );
          if (!iconPresent) {
            allDisplayed = false;
            const resolved = this.anchorService.resolveAnchor(config.anchor, config.id);
            if (resolved) {
              this.injectorService.injectIfMarked(config.id);
              // Option E: eagerly resolve hover anchor so it is cached before first click
              this.resolveHoverAnchorEagerly(config);
            }
          } else {
            // Icon is already placed — still try to pre-resolve hover if not yet cached
            this.resolveHoverAnchorEagerly(config);
          }
        });
      });

      if (allDisplayed) {
        this.stopPageLoadCheck();
        this.syncObservers();
      }
    }, CHECK_INTERVAL_MS);
  }

  private stopPageLoadCheck(): void {
    if (this.checkId !== null) {
      clearInterval(this.checkId);
      this.checkId = null;
    }
  }

  // Option E: sets data-tooltip-hover-id so showTooltip hits the cached path
  private resolveHoverAnchorEagerly(config: { id: string; hoverAnchor?: any }): void {
    if (!config.hoverAnchor) return;
    if (document.querySelector(`[data-tooltip-hover-id="${config.id}"]`)) return;
    this.anchorService.resolveAnchor(config.hoverAnchor, config.id, 'data-tooltip-hover-id', true);
  }

  // ─── Component-scoped MutationObservers ───────────────────────

  // The tooltip system's own transient UI (backdrop overlay, highlight
  // overlay, preview icon, the tooltip bubble itself, the admin toolbar/its
  // picking indicator) mutates the DOM whenever a tooltip opens/closes or an
  // admin picks an anchor. None of that is a "real" page change worth
  // reanchoring over, and reacting to it can spuriously mark unrelated,
  // perfectly-fine tooltips as Unresolved (and PUT that to the backend)
  // purely because this UI happened to mount/unmount nearby.
  private static readonly OWN_UI_SELECTOR = [
    '.tour-guide-highlighted-overlay',
    '.tooltip-element-hover-outline',
    '.tour-guide-question-icon-preview',
    '.tour-guide-question-icon',
    'app-tour-guide-tooltip',
    '.tooltip-admin-toolbar',
    '.picking-mode-indicator'
  ].join(', ');

  private isOwnUiNode(node: Node): boolean {
    return node instanceof HTMLElement && node.matches(TooltipReanchorService.OWN_UI_SELECTOR);
  }

  // Transient, app-level overlay UI — modals, dropdown portals, toasts — mounts,
  // unmounts and re-renders inside <body> constantly while the user interacts
  // with it (typing in a dropdown filter re-renders its option list on every
  // keystroke). The anchor resolver is scoped to the page component host and
  // never places icons into this UI, so reacting to its churn is pure cost — and
  // worse, a page anchor briefly covered by a modal gets marked Unresolved (and
  // PUT to the backend), which re-enters this path and storms. Any mutation that
  // originates inside one of these containers is noise.
  private static readonly TRANSIENT_OVERLAY_SELECTOR = [
    'modal-container',
    '.modal',
    '.modal-backdrop',
    'bs-modal-backdrop',
    'bs-dropdown-container',
    '.dropdown-menu',
    '.list-dropdown',
    '.simple-select-portal',
    'app-chip-select',
    '.chip-dropdown',
    '.cdk-overlay-container',
    '#toast-container',
    'toast-container',
  ].join(', ');

  private isInsideTransientOverlay(node: Node): boolean {
    const el = node instanceof HTMLElement ? node : node.parentElement;
    return !!el?.closest(TooltipReanchorService.TRANSIENT_OVERLAY_SELECTOR);
  }

  private isIgnorableMutation(records: MutationRecord[]): boolean {
    return records.every(record => {
      if (this.isInsideTransientOverlay(record.target)) return true;
      const changedNodes = [...Array.from(record.addedNodes), ...Array.from(record.removedNodes)];
      return changedNodes.length > 0 && changedNodes.every(node => this.isOwnUiNode(node));
    });
  }

  private syncObservers(): void {
    const allTooltips = this.tooltipService.getAllTooltips();
    if (allTooltips.length === 0) return;

    const currentRoute  = this.utils.normalizeRoute(this.router.url.split('?')[0]);
    const activeAnchors = allTooltips
      .filter(t => t.isEnabled !== false)
      .filter(t => t.anchor?.route === currentRoute);

    const componentTags = new Set(activeAnchors.map(t => t.anchor.componentTag));

    componentTags.forEach(tag => {
      if (this.observers.has(tag)) return;

      const hostSelector = tag === 'body' ? 'body' : tag;
      const host = document.querySelector(hostSelector);
      if (!host) return;

      const observer = new MutationObserver(
        (records) => {
          if (this.isIgnorableMutation(records)) return;
          this.scheduleReanchor(tag);
        }
      );

      observer.observe(host, { childList: true, subtree: true });
      this.observers.set(tag, observer);
    });
  }

  private disconnectStaleObservers(): void {
    this.observers.forEach(obs => obs.disconnect());
    this.observers.clear();
    this.clearReanchorTimers();
  }

  // Coalesce bursts of mutations (a dropdown re-rendering its option list, a
  // handful of *ngIf/*ngFor toggles in one CD pass) into a single reanchor pass,
  // run off the mutation-callback critical path. resolveAnchor walks the whole
  // host subtree and scores every candidate; doing that synchronously on every
  // mutation record is what makes the page janky with Hilfemodus on.
  private scheduleReanchor(componentTag: string): void {
    const existing = this.reanchorTimers.get(componentTag);
    if (existing) clearTimeout(existing);
    this.reanchorTimers.set(
      componentTag,
      setTimeout(() => {
        this.reanchorTimers.delete(componentTag);
        this.reanchorForComponent(componentTag);
      }, TooltipReanchorService.REANCHOR_DEBOUNCE_MS)
    );
  }

  private clearReanchorTimers(): void {
    this.reanchorTimers.forEach(t => clearTimeout(t));
    this.reanchorTimers.clear();
  }

  // The admin picker's own transient UI (hover highlight, preview icon) mutates
  // the DOM on every click/hover while active. That's not a "real" page change
  // worth reanchoring over, and reacting to it can spuriously mark unrelated,
  // perfectly-fine tooltips as Unresolved (and PUT that to the backend) purely
  // because picking-mode churn happened nearby. Pause observers for the
  // duration of picking, then resync (hosts may have changed) once it ends.
  setPickingActive(active: boolean): void {
    if (active) {
      this.observers.forEach(obs => obs.disconnect());
      this.clearReanchorTimers();
    } else {
      this.observers.clear();
      this.syncObservers();
    }
  }

  private reanchorForComponent(componentTag: string): void {
    const currentRoute = this.utils.normalizeRoute(this.router.url.split('?')[0]);
    const tooltips = this.tooltipService.getAllTooltips()
      .filter(t => t.isEnabled !== false)
      .filter(t => t.anchor?.componentTag === componentTag)
      .filter(t => t.anchor?.route === currentRoute);

    if (tooltips.length === 0) return;

    tooltips.forEach(config => {
      if (document.querySelector(`[data-tooltip-id="${config.id}"]`)) {
        // Icon already placed — still try to pre-resolve hover (Option E)
        this.resolveHoverAnchorEagerly(config);
        return;
      }

      const resolved = this.anchorService.resolveAnchor(config.anchor, config.id);
      if (resolved) {
        this.injectorService.injectIfMarked(config.id);
        this.resolveHoverAnchorEagerly(config); // Option E
      }
      // NOTE: intentionally NOT markAsUnresolved() here. This runs on every
      // (debounced) DOM mutation under the host, including transient page changes
      // like a modal opening over the page. A briefly-missing anchor is not a
      // broken one — and marking it Unresolved fires an HTTP PUT plus a
      // tooltips$ emit that re-enters this path. Genuine unresolved detection is
      // done by startPageLoadCheck() (wall-clock capped) and the NavigationEnd
      // reload.
    });
  }

  ngOnDestroy(): void {
    this.stopPageLoadCheck();
    this.clearReanchorTimers();
    this.observers.forEach(obs => obs.disconnect());
    this.observers.clear();
    this.routerSub?.unsubscribe();
    this.enabledSub?.unsubscribe();
  }
}

